import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../redux/reducers";
import jsPDF from "jspdf";
import Main from "../../components/custom/Main";
import PageContainer from "../../components/custom/PageContainer";
import { Table } from "react-bootstrap";
import { AppDispatch } from "../../redux/store";
import {
  fetchSalariesAction,
  fetchSalarysByEmployee,
} from "../../redux/actions/empSalaryAction";
import { Download } from "react-feather";
import { EmployeeSalary } from "../../types/EmployeeSalary";
import CollapseCard from "../../components/custom/CollapseCard";
import PayslipFilter from "../../components/PayslipFilter";
import { fetchEmployeesAction } from "../../redux/actions/employeeActions";
import { toast } from "react-toastify";
import { MonthOptions } from "../../config/functions";

const PaySlip = () => {
  const dispatch = useDispatch<AppDispatch>();
  const employeesSalary = useSelector(
    (state: StoreState) => state.employeeSalary,
  );
  const users = useSelector((state: StoreState) => state.authUser);
  const employees = useSelector((state: StoreState) => state.employee);

  const [filters, setFilters] = useState({
    employeeId: users.employeeId || "",
    year: "",
    month: "",
  });

  const [filteredSlip, setFilteredSlip] = useState<EmployeeSalary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchSalariesAction());
    dispatch(fetchEmployeesAction());
  }, [dispatch]);

  useEffect(() => {
    const savedFilters = localStorage.getItem("payslipFilter");
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(parsed);
        applyFilters(parsed);
      } catch (e) {
        console.error("Failed to parse saved filters", e);
      }
    }
  }, []);

  useEffect(() => {
    if (employeesSalary.length > 0) {
      applyLocalFilter();
    }
  }, [employeesSalary, filters]);
  const applyFilters = async (filterValues: typeof filters) => {
    setIsLoading(true);

    if (filterValues.employeeId) {
      try {
        await dispatch(
          fetchSalarysByEmployee(
            filterValues.employeeId,
            filterValues.year,
            filterValues.month,
          ),
        );

        const updatedSalaries = employeesSalary.filter(
          (slip: EmployeeSalary) => {
            const matchYear = filterValues.year
              ? String(slip.year) === String(filterValues.year)
              : true;
            const matchMonth = filterValues.month
              ? String(slip.month) === String(filterValues.month)
              : true;
            return (
              slip.employeeId === filterValues.employeeId &&
              matchYear &&
              matchMonth
            );
          },
        );

        if (updatedSalaries.length > 0) {
          setFilteredSlip(updatedSalaries);
          toast.success(`${updatedSalaries.length} record(s) found`);
        } else {
          setFilteredSlip([]);
          toast.info("No records found for selected filters");
        }
      } catch (err: unknown) {
        toast.error("Failed to fetch payslip data");
        console.error("Filter error:", err);
      } finally {
        setIsLoading(false);
      }
    } else {
      applyLocalFilter();
      setIsLoading(false);
    }
  };

  const applyLocalFilter = () => {
    let result = [...employeesSalary];

    if (users.role === "User" && users.employeeId) {
      result = result.filter((slip) => slip.employeeId === users.employeeId);
    }

    if (filters.year) {
      result = result.filter(
        (slip) => String(slip.year) === String(filters.year),
      );
    }

    if (filters.month) {
      result = result.filter(
        (slip) => String(slip.month) === String(filters.month),
      );
    }

    setFilteredSlip(result);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFindFilters = () => {
    if (!filters.year) {
      toast.warning("Please select a year");
      return;
    }

    localStorage.setItem("payslipFilter", JSON.stringify(filters));

    applyFilters(filters);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      employeeId: users.employeeId || "",
      year: "",
      month: "",
    };

    setFilters(defaultFilters);
    setFilteredSlip([]);
    localStorage.removeItem("payslipFilter");

    // Reload all data
    dispatch(fetchSalariesAction());
    toast.info("Filters reset");
  };

  const displayData =
    filters.year || filters.month
      ? filteredSlip
      : users.role === "User"
        ? employeesSalary.filter((e) => e.employeeId === users.employeeId)
        : employeesSalary;
  const downloadPayslipPDF = (emp: EmployeeSalary) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const foundEmp = employees.find((e) => e.employeeId === emp.employeeId);

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 65;
    const logoHeight = 15;
    const logoX = 30;
    const logoY = 30;

    const imgData = process.env.PUBLIC_URL
      ? process.env.PUBLIC_URL + "/img/mindzcube.png"
      : "../img/mindzcube.png";

    doc.addImage(imgData, "PNG", logoX, logoY, logoWidth, logoHeight);

    const textX = logoX + logoWidth + 15;
    let textY = logoY + 5;

    doc.setFont("times", "bold");
    doc.setFontSize(11);

    const companyTitle = "Mindzcube Software Development & Consultancy";
    doc.text(companyTitle, textX, textY);

    textY += 6;
    doc.setFontSize(10);
    const addr1 = "920A, First Floor, Baskarapuram, Thanjavur";
    doc.text(addr1, textX, textY);

    textY += 6;
    const addr2 = "Tamil Nadu, India - 614001";
    doc.text(addr2, textX, textY);

    const title = `Payslip - ${emp.month || ""} ${emp.year || ""}`;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, textY + 20);

    let y = textY + 35;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");

    doc.text(`Employee ID     : ${foundEmp?.employeeId}`, 30, y);
    doc.text(`Employee Name   : ${foundEmp?.name || "-"}`, 110, y);
    doc.text(`Department    : ${foundEmp?.department}`, 30, y + 5);
    doc.text(`Designation   : ${foundEmp?.designation || "-"}`, 110, y + 5);
    doc.text(`PAN no   : ${foundEmp?.panNumber || "-"}`, 30, y + 10);

    y += 25;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const earningsText = "Earnings";
    const earningsTextWidth = doc.getTextWidth(earningsText);
    doc.text(earningsText, (pageWidth - earningsTextWidth) / 2, y);

    const bp = emp.bp || 0;
    const hra = emp.hra || 0;
    const ta = emp.ta || 0;
    const sa = emp.sa || 0;
    const grossEarnings = bp + hra + ta + sa;

    y += 8;
    doc.setFont("helvetica", "normal");
    doc.text("Basic", 50, y);
    doc.text(`: ${bp.toFixed(2)}`, 140, y);
    y += 6;
    doc.text("H.R.A", 50, y);
    doc.text(`: ${hra.toFixed(2)}`, 140, y);
    y += 6;
    doc.text("T.A", 50, y);
    doc.text(`: ${ta.toFixed(2)}`, 140, y);
    y += 6;
    doc.text("S.A", 50, y);
    doc.text(`: ${sa.toFixed(2)}`, 140, y);
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.text("Gross Earnings", 50, y);
    doc.text(`: ${grossEarnings.toFixed(2)}`, 140, y);

    y += 15;
    doc.setFont("helvetica", "bold");
    const deductionsText = "Deductions";
    const deductionsTextWidth = doc.getTextWidth(deductionsText);
    doc.text(deductionsText, (pageWidth - deductionsTextWidth) / 2, y);

    y += 8;
    doc.setFont("helvetica", "normal");
    const tax = emp.tax || 0;
    const pf = emp.pf || 0;
    const lop = emp.lop || 0;
    const otherDeductions = tax + pf;
    y += 6;
    doc.text("LOP", 50, y);
    doc.text(`: ${lop.toFixed(2)}`, 140, y);
    y += 6;
    doc.text("Other", 50, y);
    doc.text(`: ${otherDeductions.toFixed(2)}`, 140, y);

    const grossDeduction = lop + otherDeductions;
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.text("Gross Deductions", 50, y);
    doc.text(`: ${grossDeduction.toFixed(2)}`, 140, y);

    y += 10;
    doc.setFont("helvetica", "bold");
    const netPay = grossEarnings - grossDeduction;
    doc.text("netPay", 50, y);
    doc.text(`: ${netPay.toFixed(2)}`, 140, y);

    y += 20;
    doc.setFont("times", "normal");
    doc.setFontSize(9);

    const remarks =
      "Remarks: This is a computer generated payslip and does not require signature.";
    doc.text(remarks, pageWidth / 2, y, { align: "center" });

    doc.save("Payslip.pdf");
  };

  useEffect(() => {
    dispatch(fetchEmployeesAction());
  }, []);

  return (
    <Main>
      <PageContainer title="PaySlip">
        <CollapseCard title="Payslip Filter">
          <PayslipFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFind={handleFindFilters}
            onReset={handleResetFilters}
          />
        </CollapseCard>

        <CollapseCard title="Payslip List">
          {isLoading ? (
            <div className="text-center py-4">Loading...</div>
          ) : displayData.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No payslip records found. Try adjusting your filters.
            </div>
          ) : (
            <Table className="custom-employee-table" responsive>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Year</th>
                  <th>Month</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((log, i) => (
                  <tr key={log.id || i}>
                    <td>{i + 1}</td>
                    <td>{log.year}</td>
                    <td>
                      {MonthOptions().find((m) => m.value === log.month)
                        ?.label || log.month}
                    </td>
                    <td>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => downloadPayslipPDF(log)}
                      >
                        <Download style={{ width: "15px" }} /> Download Payslip
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default PaySlip;
